import React, { useState } from 'react';
import { Box, Collapse, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CategoryTag, PaidBadge, StatusPill } from '../common/ui';
import { fmtDate } from '../../utils/format';
import { useAuth } from '../../contexts/AuthContext';
import { hasInvestor, hasParcelNumbers } from '../../utils/workflows';

const headSx = { fontWeight: 700, fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.6, lineHeight: 1.35, py: 1.25, px: 1.5, bgcolor: '#F8FAFC', borderBottom: '1px solid #EEF2F6', verticalAlign: 'bottom' };
const cellSx = { fontSize: 13, color: '#475569', whiteSpace: 'nowrap', px: 1.5 };
const dash = <Typography component="span" sx={{ fontSize: 13, color: '#CBD5E1' }}>—</Typography>;

/** What the expandable row shows: owner and every co-owner first, then the other survey details. */
const otherSurvey = (j, t) => [
    [t('jobs.fields.ownerName'), j.ownerName],
    [t('jobs.fields.coOwners'), j.coOwners?.length ? j.coOwners.map(n => <span key={n}>{n}<br /></span>) : null],
    [t('jobs.fields.municipality'), j.municipality],
    [t('jobs.fields.cadastralZone'), j.cadastralZone],
    [t('jobs.fields.propertySheet'), j.propertySheet],
    [t('jobs.fields.parcelNumber'), j.parcelNumber],
    hasParcelNumbers(j.category) && [t('jobs.fields.parentParcel'), j.parentParcel],
    hasParcelNumbers(j.category) && [t('jobs.fields.newParcels'), j.newParcels],
    hasInvestor(j.category) && [t('jobs.fields.investor'), j.investor],
].filter(x => x && x[1]);

function SurveyDetails({ items }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 1.5, px: 2, py: 1.5, bgcolor: '#F8FAFC' }}>
            {items.map(([label, value]) => (
                <Box key={label} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13, color: '#1E293B', wordBreak: 'break-word' }}>{value}</Typography>
                </Box>
            ))}
        </Box>
    );
}

/**
 * Owner (labelled, so it is clear who it is) and the first co-owner with a "+N" count; the expanded row lists them all.
 * Without owners: the cadastral zone and parcel.
 */
function OwnersCell({ job, t }) {
    const co = job.coOwners || [];
    if (!job.ownerName && !co.length) {
        const place = [job.cadastralZone, job.parcelNumber && `${t('jobs.parcelShort')} ${job.parcelNumber}`].filter(Boolean).join(' · ');
        return place ? <Typography noWrap sx={{ fontSize: 13, color: '#475569' }}>{place}</Typography> : dash;
    }
    const tag = { fontSize: 11, fontWeight: 700, color: '#94A3B8', mr: 0.5 };
    return (
        <Box sx={{ minWidth: 0 }}>
            {job.ownerName && (
                <Typography noWrap sx={{ fontSize: 13, color: '#1E293B', fontWeight: 600 }}>
                    <Box component="span" sx={tag}>{t('jobs.fields.ownerName')}</Box>{job.ownerName}
                </Typography>
            )}
            {co.length > 0 && (
                <Typography noWrap sx={{ fontSize: 12, color: '#64748B' }}>
                    <Box component="span" sx={tag}>{t('jobs.fields.coOwners')}</Box>
                    {co[0]}{co.length > 1 && <Box component="span" sx={{ fontWeight: 700, color: '#427898', ml: 0.5 }}>+{co.length - 1}</Box>}
                </Typography>
            )}
        </Box>
    );
}

export default function JobsTable({ jobs, total, page, size, onPage, onSize, hideClient = false }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [open, setOpen] = useState({});
    const columns = 7 + (hideClient ? 0 : 1) + (isAdmin ? 1 : 0);
    const toggle = (e, id) => { e.stopPropagation(); setOpen(o => ({ ...o, [id]: !o[id] })); };

    return (
        <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '14px', overflow: 'hidden' }}>
            <TableContainer>
                <Table sx={{ minWidth: 1000 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}>{t('jobs.fields.number')}</TableCell>
                            <TableCell sx={headSx} colSpan={2}>{t('jobs.columns.survey')}</TableCell>
                            {!hideClient && <TableCell sx={headSx}>{t('jobs.columns.client')}</TableCell>}
                            <TableCell sx={headSx}>{t('jobs.columns.category')}</TableCell>
                            <TableCell sx={headSx}>{t('jobs.columns.submitted')}</TableCell>
                            <TableCell sx={headSx}>{t('jobs.columns.cadastreCase')}</TableCell>
                            <TableCell sx={headSx}>{t('jobs.columns.status')}</TableCell>
                            {isAdmin && <TableCell sx={headSx} align="right">{t('jobs.columns.payment')}</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {jobs.map(j => {
                            const details = otherSurvey(j, t);
                            // Only when the row adds something the cell does not show (more co-owners, other survey details).
                            const expandable = (j.coOwners?.length || 0) > 1 || details.length > (j.ownerName ? 1 : 0) + (j.coOwners?.length ? 1 : 0);
                            const isOpen = !!open[j.id];
                            return (
                                <React.Fragment key={j.id}>
                                    <TableRow hover onClick={() => navigate(`/jobs/${j.id}`)}
                                              sx={{ cursor: 'pointer', '& td': { borderBottom: isOpen ? 0 : undefined } }}>
                                        <TableCell sx={{ py: 1.25, px: 1.5, maxWidth: 200 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Typography noWrap sx={{ fontWeight: 700, color: '#0F172A', fontSize: 13.5 }}>{j.number || '—'}</Typography>
                                                {j.progress && (
                                                    <Tooltip title={t('workflow.progress', { done: j.progress.done, total: j.progress.total })}>
                                                        <Box component="span" sx={{
                                                            fontSize: 11, fontWeight: 700, px: 0.75, borderRadius: '6px', flexShrink: 0,
                                                            color: j.progress.done === j.progress.total ? '#059669' : '#64748B',
                                                            bgcolor: j.progress.done === j.progress.total ? 'rgba(16,185,129,0.10)' : '#F1F5F9',
                                                        }}>{j.progress.done}/{j.progress.total}</Box>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            {/* Elaborate date under the case number; the job name is in the tooltip. */}
                                            <Tooltip title={j.title} placement="bottom-start">
                                                <Typography noWrap sx={{ fontSize: 12, color: '#94A3B8' }}>
                                                    {j.processedOn ? `${t('jobs.columns.elaborateShort')}: ${fmtDate(j.processedOn)}` : j.title}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ px: 1.5, pr: 0, maxWidth: 220 }}><OwnersCell job={j} t={t} /></TableCell>
                                        <TableCell sx={{ width: 36, pl: 0.25, pr: 1 }}>
                                            {expandable && (
                                                <Tooltip title={t('jobs.columns.showSurvey')}>
                                                    <IconButton size="small" onClick={(e) => toggle(e, j.id)} aria-label={t('jobs.columns.showSurvey')} aria-expanded={isOpen}>
                                                        <KeyboardArrowDownIcon fontSize="small" sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        {!hideClient && <TableCell sx={{ ...cellSx, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.client?.name}</TableCell>}
                                        <TableCell sx={{ px: 1.5, maxWidth: 170 }}><CategoryTag category={j.category} /></TableCell>
                                        <TableCell sx={cellSx}>{j.steps?.SUBMITTED ? fmtDate(j.steps.SUBMITTED) : dash}</TableCell>
                                        <TableCell sx={cellSx}>{j.cadastreCaseNumber || dash}</TableCell>
                                        <TableCell sx={{ px: 1.5, whiteSpace: 'nowrap' }}><StatusPill status={j.status} size="sm" /></TableCell>
                                        {/* Paid or not only; amounts are shown inside the job, after the password. */}
                                        {isAdmin && (
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                {j.priced ? <PaidBadge paid={j.paid} /> : dash}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                    {expandable && (
                                        <TableRow>
                                            <TableCell colSpan={columns} sx={{ p: 0, borderBottom: isOpen ? undefined : 0 }}>
                                                <Collapse in={isOpen} unmountOnExit><SurveyDetails items={details} /></Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {onPage && (
                <TablePagination component="div" count={total} page={page} rowsPerPage={size}
                                 onPageChange={(_, p) => onPage(p)} onRowsPerPageChange={(e) => onSize(parseInt(e.target.value, 10))}
                                 rowsPerPageOptions={[10, 25, 50, 100]} sx={{ borderTop: '1px solid #F1F5F9' }} />
            )}
        </Paper>
    );
}
